import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Wrench } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try { await login(email, password); navigate("/", { replace: true }); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-zinc-950">
      <img src="https://images.unsplash.com/photo-1751054631354-a42bd7609d75" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-black/80" />
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent" />

      <form onSubmit={submit} className="relative z-10 mx-4 w-full max-w-md animate-fade-up rounded-md border border-zinc-800 bg-zinc-900/90 p-8 backdrop-blur">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-amber-500">
            <Wrench size={24} weight="bold" className="text-zinc-950" />
          </div>
          <div>
            <div className="font-display text-xl font-extrabold tracking-tight leading-none">APEX MINERAL RESOURCES</div>
          </div>
        </div>

        <h1 className="font-display text-3xl font-extrabold tracking-tight text-zinc-50">SIGN IN</h1>
        <p className="mt-2 text-sm text-zinc-400">Sign in with your account to manage the fleet.</p>

        <div className="mt-6 space-y-4">
          <div>
            <Label className="text-xs text-zinc-400">Email</Label>
            <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com" className="mt-1 bg-zinc-950" required />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Password</Label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" className="mt-1 bg-zinc-950" required />
          </div>
        </div>

        {error && <div data-testid="login-error" className="mt-4 rounded-sm border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</div>}

        <button data-testid="login-submit" type="submit" disabled={busy}
          className="mt-6 w-full rounded-sm bg-amber-500 py-3.5 font-bold text-zinc-950 transition-colors hover:bg-amber-400 disabled:opacity-60">
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}
